import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDebounce } from 'use-debounce';
import { z } from 'zod';
import CustomSelect from '@/components/shared/CustomSelect/CustomSelect';
import { Input } from '@/components/shared/Input/Input';
import { MultiComboBox } from '@/components/shared/MultiComboBox/MultiComboBox';
import { currencyOptions, formatCurrency } from '@/lib/select-options';
import { Button } from '@/components/ui/button';
import { SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Info, Loader2 } from 'lucide-react';
import { Label } from '@/components/shared/Label/Label';
import { useTranslation } from 'react-i18next';
import {
  APPLY_TO_ALL_CURRENCIES,
  SHIPPING_ALL_VALUE,
  ShippingAuxOption,
  ShippingConfiguration,
  ShippingConfigurationPayload,
  shippingNamedItemsToIds,
} from '@/@types/ShippingConfigurations';
import { ShippingConfigurationsService } from '@/services/shipping-configurations.service';
import {
  expandShippingRegionsToCountryCodes,
  fetchCountryOptionsFromJson,
  getAllCountryAlpha2Codes,
  getCurrenciesForCountryCodes,
} from '@/utils/shippingCountries';
export type { ShippingConfiguration, ShippingConfigurationPayload };
type ShippingConfigurationFormValues = {
  name: string;
  offers: string[];
  countries: string[];
  amount: number;
  amountCurrency: string;
  status: boolean;
};
type Props = {
  isOpen: boolean;
  mode: 'create' | 'edit';
  initialValues?: ShippingConfiguration | null;
  onClose: () => void;
  onSubmit: (payload: ShippingConfigurationPayload) => Promise<void> | void;
};
function toValueArray(values: string[] | { value?: string }[]) {
  if (!Array.isArray(values)) return [];
  return values
    .map((v) => (typeof v === 'string' ? v : String(v.value ?? '')))
    .filter(Boolean);
}
function expandAllSelection(values: string[], allValues: string[]): string[] {
  if (!values.includes(SHIPPING_ALL_VALUE)) return values;
  return allValues;
}
function toPayloadWithAll(
  values: string[],
  allValues: string[],
): string[] {
  if (values.includes(SHIPPING_ALL_VALUE)) return [SHIPPING_ALL_VALUE];
  if (
    allValues.length > 0 &&
    values.length === allValues.length &&
    allValues.every((value) => values.includes(value))
  ) {
    return [SHIPPING_ALL_VALUE];
  }
  return values;
}
function countriesToForm(
  countries: ShippingConfiguration['countries'],
): string[] {
  return countries.map((country) =>
    country === SHIPPING_ALL_VALUE
      ? SHIPPING_ALL_VALUE
      : String(country).toUpperCase(),
  );
}
function countriesFromForm(
  countries: string[],
  allCountryCodes: string[],
): ShippingConfigurationPayload['countries'] {
  const expandedCountries = expandShippingRegionsToCountryCodes(
    countries.map((code) => code.toUpperCase()),
  );
  return toPayloadWithAll(expandedCountries, allCountryCodes);
}
function currenciesFromCountries(
  countries: string[],
  allCountryCodes: string[],
): ShippingConfigurationPayload['currencies'] {
  const countriesPayload = countriesFromForm(countries, allCountryCodes);
  if (countriesPayload.includes(SHIPPING_ALL_VALUE)) {
    return [APPLY_TO_ALL_CURRENCIES];
  }
  const expandedCountries = expandShippingRegionsToCountryCodes(
    countries.map((code) => code.toUpperCase()),
  );
  return getCurrenciesForCountryCodes(expandedCountries);
}
export default function ShippingConfigurationForm({
  isOpen,
  mode,
  initialValues,
  onClose,
  onSubmit,
}: Props) {
  const { t } = useTranslation(['common', 'sales']);
  const defaultAmountCurrency = 'BRL';
  const previousAmount =
    mode === 'edit' ? Math.max(0, Number(initialValues?.amount) || 0) : 0;
  const previousAmountCurrency =
    mode === 'edit'
      ? String(initialValues?.amountCurrency ?? defaultAmountCurrency)
      : '';
  const maxAllowedAmountCents =
    mode === 'edit' && previousAmount > 0
      ? Math.floor(previousAmount * 1.5)
      : null;
  const formSchema = useMemo(
    () =>
      z
        .object({
          name: z.string().trim().min(1, t('common:required')),
          offers: z.array(z.string()).min(1, t('common:required')),
          countries: z.array(z.string()).min(1, t('common:required')),
          amount: z.coerce.number().min(0).default(0),
          amountCurrency: z.string().min(1).default('BRL'),
          status: z.boolean().default(true),
        })
        .superRefine((values, ctx) => {
          if (mode !== 'edit' || previousAmount <= 0) {
            return;
          }
          if (
            values.amountCurrency &&
            previousAmountCurrency &&
            values.amountCurrency.toUpperCase() !==
              previousAmountCurrency.toUpperCase()
          ) {
            return;
          }
          const maxAllowed = Math.floor(previousAmount * 1.5);
          if (values.amount > maxAllowed) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['amount'],
              message: t('common:shipping_configurations_amount_increase_cap'),
            });
          }
        }),
    [mode, previousAmount, previousAmountCurrency, t],
  );
  const amountCurrencyOptions = useMemo(
    () =>
      currencyOptions.filter((option) =>
        ['BRL', 'USD'].includes(String(option.value)),
      ),
    [],
  );
  const allCountryCodes = useMemo(() => getAllCountryAlpha2Codes(), []);
  const defaultValues = useMemo<ShippingConfigurationFormValues>(() => {
    if (initialValues) {
      const offerIds = initialValues.offers?.length
        ? shippingNamedItemsToIds(initialValues.offers)
        : [];
      const countryIds = expandAllSelection(
        countriesToForm(initialValues.countries ?? []),
        allCountryCodes,
      );
      return {
        name: initialValues.name ?? '',
        offers: offerIds,
        countries: countryIds,
        amount: initialValues.amount ?? 0,
        amountCurrency: initialValues.amountCurrency ?? defaultAmountCurrency,
        status: initialValues.status ?? true,
      };
    }
    return {
      name: '',
      offers: [],
      countries: [],
      amount: 0,
      amountCurrency: defaultAmountCurrency,
      status: true,
    };
  }, [initialValues, defaultAmountCurrency, allCountryCodes]);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors },
    clearErrors,
  } = useForm<ShippingConfigurationFormValues>({
    resolver: zodResolver(formSchema),
    shouldUnregister: false,
    defaultValues,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formattedAmount, setFormattedAmount] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [debouncedSelectedProductIds] = useDebounce(selectedProductIds, 300);
  const isEditing = isOpen;
  const isProductsAll = selectedProductIds.includes(SHIPPING_ALL_VALUE);
  const watchedOffers = watch('offers') ?? [];
  const watchedCountries = watch('countries') ?? [];
  const watchedStatus = watch('status');
  const watchedAmount = watch('amount');
  const watchedAmountCurrency = watch('amountCurrency');
  const isOffersAll = watchedOffers.includes(SHIPPING_ALL_VALUE);
  const isCountriesAll =
    watchedCountries.includes(SHIPPING_ALL_VALUE) ||
    (allCountryCodes.length > 0 &&
      watchedCountries.length === allCountryCodes.length &&
      allCountryCodes.every((code) => watchedCountries.includes(code)));
  const debouncedIsProductsAll = debouncedSelectedProductIds.includes(
    SHIPPING_ALL_VALUE,
  );
  const offersByProductsQueryKey = useMemo(() => {
    if (debouncedIsProductsAll) return ['shipping-offers-by-products', 'ALL'];
    const sorted = [...debouncedSelectedProductIds]
      .filter((id) => id !== SHIPPING_ALL_VALUE)
      .sort();
    return ['shipping-offers-by-products', sorted.join(',')];
  }, [debouncedSelectedProductIds, debouncedIsProductsAll]);
  const { data: availableOffers = [], isFetched: offersByProductsFetched } =
    useQuery({
      queryKey: offersByProductsQueryKey,
      queryFn: async () => {
        if (!debouncedSelectedProductIds.length) return [];
        if (debouncedIsProductsAll) {
          return ShippingConfigurationsService.getOffersByProductIds({
            productIds: [],
            allProducts: true,
          });
        }
        return ShippingConfigurationsService.getOffersByProductIds({
          productIds: debouncedSelectedProductIds.filter(
            (id) => id !== SHIPPING_ALL_VALUE,
          ),
        });
      },
      enabled: isOpen && debouncedSelectedProductIds.length > 0,
      staleTime: 60 * 1000,
    });
  const availableOfferIds = useMemo(
    () => availableOffers.map((offer) => offer.value),
    [availableOffers],
  );
  const isOffersSelectAllActive = isProductsAll
    ? isOffersAll
    : availableOfferIds.length > 0 &&
      watchedOffers.length === availableOfferIds.length &&
      availableOfferIds.every((id) => watchedOffers.includes(id));
  const previousProductIdsLengthRef = useRef(0);
  useEffect(() => {
    if (!isOpen) return;
    reset(defaultValues);
    const productIds = initialValues?.products?.length
      ? shippingNamedItemsToIds(initialValues.products)
      : [];
    setSelectedProductIds(
      productIds.some((p) => p.toUpperCase() === SHIPPING_ALL_VALUE)
        ? [SHIPPING_ALL_VALUE]
        : productIds,
    );
    setFormattedAmount(
      formatCurrency(defaultValues.amount / 100, defaultValues.amountCurrency),
    );
  }, [defaultValues, isOpen, reset, initialValues?.products]);
  useEffect(() => {
    if (!isOpen) {
      previousProductIdsLengthRef.current = 0;
    }
  }, [isOpen]);
  useEffect(() => {
    if (!watchedAmountCurrency) return;
    setFormattedAmount(
      formatCurrency((watchedAmount ?? 0) / 100, watchedAmountCurrency),
    );
  }, [watchedAmountCurrency, watchedAmount]);
  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value.replace(/\D/g, '');
    const valueInCents = parseInt(rawValue || '0', 10);
    setValue('amount', valueInCents);
    setFormattedAmount(
      formatCurrency(valueInCents / 100, watchedAmountCurrency || 'BRL'),
    );
    clearErrors('amount');
  };
  const fetchProductOptions = useCallback(async (query?: string) => {
    return ShippingConfigurationsService.getProducts(query);
  }, []);
  const fetchOffersOptions = useCallback(
    async (query: string) => {
      if (!selectedProductIds.length) {
        return [];
      }
      return ShippingConfigurationsService.getOffersByProductIds({
        productIds: selectedProductIds.filter((id) => id !== SHIPPING_ALL_VALUE),
        q: query,
        allProducts: isProductsAll,
      });
    },
    [selectedProductIds, isProductsAll],
  );
  useEffect(() => {
    if (!isOpen) return;
    const previousLength = previousProductIdsLengthRef.current;
    previousProductIdsLengthRef.current = selectedProductIds.length;
    if (previousLength > 0 && selectedProductIds.length === 0) {
      setValue('offers', []);
    }
  }, [isOpen, selectedProductIds.length, setValue]);
  useEffect(() => {
    if (!isOpen || !debouncedSelectedProductIds.length) return;
    if (!offersByProductsFetched) return;
    const currentOffers = getValues('offers') ?? [];
    if (currentOffers.includes(SHIPPING_ALL_VALUE)) return;
    const filteredOffers = currentOffers.filter((offerId) =>
      availableOfferIds.includes(offerId),
    );
    if (filteredOffers.length !== currentOffers.length) {
      setValue('offers', filteredOffers);
    }
  }, [
    isOpen,
    debouncedSelectedProductIds.length,
    availableOfferIds,
    offersByProductsFetched,
    getValues,
    setValue,
  ]);
  const fetchCountriesOptions = useCallback(
    async (query: string) => fetchCountryOptionsFromJson(query),
    [],
  );
  const handleProductsSelectAll = () => {
    if (isProductsAll) {
      setSelectedProductIds([]);
      return;
    }
    setSelectedProductIds([SHIPPING_ALL_VALUE]);
  };
  const handleOffersSelectAll = () => {
    if (isOffersSelectAllActive) {
      setValue('offers', []);
      clearErrors('offers');
      return;
    }
    if (isProductsAll) {
      setValue('offers', [SHIPPING_ALL_VALUE]);
      clearErrors('offers');
      return;
    }
    setValue('offers', [...availableOfferIds]);
    clearErrors('offers');
  };
  const handleCountriesSelectAll = () => {
    if (isCountriesAll) {
      setValue('countries', []);
      clearErrors('countries');
      return;
    }
    setValue('countries', [...allCountryCodes]);
    clearErrors('countries');
  };
  const handleProductsChange = (values: string[] | { value?: string }[]) => {
    const incoming = toValueArray(values).filter(
      (value) => value !== SHIPPING_ALL_VALUE,
    );
    setSelectedProductIds(incoming);
  };
  const onValidSubmit = async (values: ShippingConfigurationFormValues) => {
    setIsSubmitting(true);
    try {
      const productsPayload = isProductsAll
        ? [SHIPPING_ALL_VALUE]
        : selectedProductIds;
      let offersPayload = values.offers;
      if (isOffersAll) {
        offersPayload = isProductsAll ? [SHIPPING_ALL_VALUE] : values.offers;
      }
      const countriesPayload = countriesFromForm(
        values.countries,
        allCountryCodes,
      );
      const payload: ShippingConfigurationPayload = {
        name: values.name.trim(),
        products: productsPayload,
        offers: offersPayload,
        countries: countriesPayload,
        currencies: currenciesFromCountries(values.countries, allCountryCodes),
        minQuantity: 1,
        amount: values.amount,
        amountCurrency: values.amountCurrency,
        status: values.status,
      };
      await onSubmit(payload);
      onClose();
    } catch {
      // Toast de erro tratado no container (Shipments/index.tsx).
    } finally {
      setIsSubmitting(false);
    }
  };
  const productLabelById = useMemo(() => {
    const map = new Map<string, string>();
    (initialValues?.products ?? []).forEach((item) => {
      map.set(item.id, item.name || item.id);
    });
    return map;
  }, [initialValues?.products]);
  const offerLabelById = useMemo(() => {
    const map = new Map<string, string>();
    (initialValues?.offers ?? []).forEach((item) => {
      map.set(item.id, item.name || item.id);
    });
    availableOffers.forEach((item) => {
      map.set(item.value, item.label);
    });
    return map;
  }, [initialValues?.offers, availableOffers]);
  const productValues: ShippingAuxOption[] = isProductsAll
    ? [{ value: SHIPPING_ALL_VALUE, label: t('common:all') }]
    : selectedProductIds.map((id) => ({
        value: id,
        label: productLabelById.get(id) || id,
      }));
  const offerValues: ShippingAuxOption[] = isOffersAll
    ? [{ value: SHIPPING_ALL_VALUE, label: t('common:all') }]
    : watchedOffers.map((id) => ({
        value: id,
        label: offerLabelById.get(id) || id,
      }));
  const showOffersSelectionHint =
    mode === 'create' && selectedProductIds.length > 0;
  return (
    <section
      className="h-full flex flex-col justify-between"
      data-cy="shipping-config-form"
    >
      <SheetHeader className="md:p-6 p-4">
        <SheetTitle className="flex gap-2">
          {mode === 'create'
            ? t('common:shipping_configurations_create_title')
            : t('common:shipping_configurations_edit_title')}
        </SheetTitle>
      </SheetHeader>
      <form
        onSubmit={handleSubmit(onValidSubmit)}
        className="flex flex-col justify-between h-full"
      >
        <section className="flex relative flex-col gap-4 px-4 md:px-6 mb-24">
          <div className="flex items-center gap-3">
            <Switch
              checked={watchedStatus}
              onCheckedChange={(checked) => setValue('status', checked)}
              aria-label={t('common:status')}
            />
            <Label>{t('common:status')}</Label>
          </div>
          <Input
            {...register('name')}
            label={t('common:name_single')}
            placeholder={t('common:title_placeholder')}
            helperText={errors.name?.message}
            data-cy="input-shipping-config-name"
          />
          <MultiComboBox
            modal={false}
            fetchOptions={fetchProductOptions}
            label={t('common:products')}
            placeholder={t('sales:products_placeholder')}
            values={productValues}
            returnFullObject
            onChange={handleProductsChange}
            onSelectAll={handleProductsSelectAll}
            isSelectAllActive={isProductsAll}
            selectAllOptions
            queryKey="shipping-products"
            isEditing={isEditing}
            cypressName="shipping-products-multi"
          />
          {showOffersSelectionHint && (
            <Alert variant="info" data-cy="shipping-offers-hint">
              <Info className="h-5 w-5 flex-shrink-0" />
              <div>
                <AlertTitle>
                  {t('common:shipping_configurations_offers_hint_title')}
                </AlertTitle>
                <AlertDescription>
                  {isProductsAll
                    ? t('common:shipping_configurations_offers_hint_all_products')
                    : t('common:shipping_configurations_offers_hint_description')}
                </AlertDescription>
              </div>
            </Alert>
          )}
          <MultiComboBox
            modal={false}
            fetchOptions={fetchOffersOptions}
            label={t('common:offers')}
            placeholder={t('sales:offer_placeholder')}
            values={offerValues}
            returnFullObject
            onChange={(values) => {
              const next = toValueArray(values).filter(
                (value) => value !== SHIPPING_ALL_VALUE,
              );
              setValue('offers', next);
              clearErrors('offers');
            }}
            onSelectAll={handleOffersSelectAll}
            isSelectAllActive={isOffersSelectAllActive}
            selectAllOptions
            helperText={errors.offers?.message}
            queryKey="shipping-offers"
            newParams={selectedProductIds}
            disabled={!selectedProductIds.length}
            isEditing={isEditing}
            cypressName="shipping-offers-multi"
          />
          <MultiComboBox
            modal={false}
            fetchOptions={fetchCountriesOptions}
            label={t('common:countries')}
            placeholder={t('common:origin_country_placeholder')}
            values={watchedCountries}
            onChange={(values) => {
              setValue('countries', toValueArray(values));
              clearErrors('countries');
            }}
            selectAllOptions
            onSelectAll={handleCountriesSelectAll}
            isSelectAllActive={isCountriesAll}
            helperText={errors.countries?.message}
            queryKey="shipping-countries"
            isEditing={isEditing}
            dataCy="shipping-countries-multi"
          />
          <div className="flex flex-col gap-1 w-full">
            <div className="flex gap-4 items-end w-full md:max-w-[300px]">
              <Input
                value={formattedAmount}
                onChange={handleAmountChange}
                label={t('common:value')}
                placeholder={t('common:currency_value_placeholder')}
                data-cy="input-shipping-amount"
              />
              <div className="w-[100px]">
                <CustomSelect
                  className="w-[56px]"
                  options={amountCurrencyOptions}
                  placeholder=""
                  data-cy="select-shipping-amount-currency"
                  onValueChange={(value) => {
                    setValue('amountCurrency', value);
                    clearErrors('amountCurrency');
                  }}
                  value={watchedAmountCurrency}
                />
              </div>
            </div>
            {errors.amount?.message && (
              <p className="text-sm text-error">{errors.amount.message}</p>
            )}
            {mode === 'edit' &&
              maxAllowedAmountCents != null &&
              watchedAmountCurrency?.toUpperCase() ===
                previousAmountCurrency.toUpperCase() && (
                <p className="text-xs text-muted">
                  {t('common:shipping_configurations_amount_increase_hint')}
                </p>
              )}
          </div>
        </section>
        <SheetFooter className="flex z-50 sticky bottom-0 bg-[#09090b] w-full border-t border-t-colorBorder p-4 items-end mt-4 justify-end">
          <div className="flex w-full z-50 gap-2 justify-end items-end">
            <Button
              variant="ghost"
              className="w-full !text-colorText md:max-w-[118px]"
              onClick={onClose}
              type="button"
              disabled={isSubmitting}
            >
              {t('common:cancel')}
            </Button>
            <Button
              variant="default"
              className="w-full md:max-w-fit"
              type="submit"
              disabled={isSubmitting}
              data-cy="save-shipping-config"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="animate-spin" />
                  {t('common:saving')}
                </span>
              ) : mode === 'create' ? (
                t('common:save')
              ) : (
                t('common:update')
              )}
            </Button>
          </div>
        </SheetFooter>
      </form>
    </section>
  );
}
